// 공용 DataGrid 래퍼.
// 프로젝트 기본값(행 밀도 / 페이지 크기 / 툴바) + 빈·에러 상태 + Paper 컨테이너를 제공한다.
// localeText는 전역 테마(createAppTheme의 3번째 인자 koKR)가 담당하므로 여기서 넘기지 않는다.
// className은 그대로 <DataGrid> 루트로 전달되고, panelSx는 컨테이너 Paper에 적용된다.
// sx/onRowClick은 getRowShowPath(useRowShowNavigation)가 쓰는 값과 합성돼서 전달된다 — 그대로 전달이 아님.
// renderCell 미지정 컬럼은 말줄임 시 툴팁(OverflowTooltip) 자동 적용 — 제외하려면 자체 renderCell 지정

import React, { useMemo } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import type { DataGridProps } from "@mui/x-data-grid";
import type { SxProps, Theme } from "@mui/material/styles";
import { NoRowsOverlay } from "./overlays";
import { CustomPagination } from "./CustomPagination";
import { useRowShowNavigation } from "./useRowShowNavigation";
import { withOverflowTooltip } from "./OverflowTooltip";

export interface DataGridPanelProps extends DataGridProps {
  isError?: boolean;
  errorMessage?: string;
  emptyMessage?: string;
  emptyHint?: string;
  // 그리드 영역 높이. 미지정 시 부모 flex column(main > Card > CardContent)의
  // 남은 공간을 채우고, 그리드 바디는 그리드 내부에서 세로 스크롤한다(권장).
  // 고정 높이가 필요한 호출부만 명시적으로 넘긴다.
  height?: number | string;
  // 컨테이너 Paper override slot
  panelSx?: SxProps<Theme>;
  // 지정 시 행 클릭으로 해당 경로로 이동한다(useRowShowNavigation 참고).
  // 체크박스/액션 버튼 클릭, 텍스트 드래그 선택 중에는 발동하지 않는다.
  getRowShowPath?: (row: any) => string | null | undefined;
}

// 안정적인 컴포넌트 참조 — 인라인 화살표로 넘기면 매 렌더 remount 된다.
const EmptyOverlay: React.FC = () => null;

export const DataGridPanel: React.FC<DataGridPanelProps> = ({
  isError = false,
  errorMessage = "목록을 불러오지 못했습니다.",
  emptyMessage = "조건에 맞는 데이터가 없습니다.",
  emptyHint = "검색 조건을 조정해 보세요.",
  height,
  panelSx,
  slots,
  slotProps,
  pageSizeOptions,
  getRowShowPath,
  onRowClick: callerOnRowClick,
  sx: callerSx,
  columns,
  ...dataGridProps
}) => {
  const resolvedPageSizeOptions = pageSizeOptions ?? [20, 50, 100];
  const tooltipColumns = useMemo(() => withOverflowTooltip(columns), [columns]);
  // getRowShowPath/onRowClick/sx를 위에서 destructure했으므로 ...dataGridProps로
  // <DataGrid>에 그대로 흘러가지 않는다. onRowClick/sx는 아래에서 caller 값과
  // 합성(덮어쓰기 아님)해서 다시 전달한다.
  const { onRowClick: navOnRowClick, sx: navSx } = useRowShowNavigation(getRowShowPath);

  // 에러일 때는 상단 Alert만 노출하고 "데이터 없음" 오버레이는 억제한다.
  // pagination은 기본 화살표 방식 대신 페이지 번호 + 직접 이동이 가능한 커스텀 컴포넌트로 교체.
  const mergedSlots = {
    toolbar: GridToolbar,
    noRowsOverlay: isError ? EmptyOverlay : NoRowsOverlay,
    pagination: CustomPagination,
    ...slots,
  };

  // slot 키별로 병합(caller의 toolbar가 통째 교체되지 않도록)
  const mergedSlotProps = {
    ...slotProps,
    noRowsOverlay: {
      message: emptyMessage,
      hint: emptyHint,
      ...(slotProps?.noRowsOverlay as object | undefined),
    },
    pagination: {
      pageSizeOptions: resolvedPageSizeOptions,
      ...(slotProps?.pagination as object | undefined),
    },
  } as DataGridProps["slotProps"];

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        // 부모 flex column의 남은 공간을 채운다(App.tsx GlobalStyles 참고).
        display: "flex",
        flexDirection: "column",
        flex: "1 1 0",
        minHeight: 0,
        ...panelSx,
      }}
    >
      {isError && (
        <Alert severity="error" square sx={{ borderRadius: 0 }}>
          {errorMessage}
        </Alert>
      )}
      <Box
        sx={{
          // height가 오면 고정, 아니면 flex로 남은 공간을 채워 그리드 내부 스크롤을 유도한다.
          // minHeight는 좁은 뷰포트(검색 카드가 큰 화면 등)에서 그리드가 0으로
          // 수렴해 DataGrid의 "empty height" 경고가 뜨는 것을 막는 하한선이다.
          ...(height != null ? { height } : { flex: "1 1 0", minHeight: 320 }),
          width: "100%",
        }}
      >
        <DataGrid
          // 접근성 폰트 확대(2026-09)에 맞춰 텍스트가 잘리지 않도록 행/헤더 높이도 함께 상향.
          rowHeight={48}
          columnHeaderHeight={60}
          disableRowSelectionOnClick
          {...dataGridProps}
          columns={tooltipColumns}
          pageSizeOptions={resolvedPageSizeOptions}
          slots={mergedSlots}
          slotProps={mergedSlotProps}
          onRowClick={(params, event, details) => {
            callerOnRowClick?.(params, event, details);
            navOnRowClick?.(params, event, details);
          }}
          // navSx가 false일 수 있어(getRowShowPath 미지정 시) 배열 원소에 undefined가
          // 들어가면 SxProps 타입 에러 — false는 허용되므로 ?? false로 방어.
          sx={[
            navSx ?? false,
            ...(Array.isArray(callerSx) ? callerSx : callerSx ? [callerSx] : []),
          ]}
        />
      </Box>
    </Paper>
  );
};
